import React from 'react';

function PrivacyPolicy() {
  const sections = [
    {
      title: '1. Information We Collect',
      content: [
        'We collect information that you provide directly to us, including when you create an account, subscribe to our services, contact us for support, or communicate with us. This may include your name, email address, phone number, company name, and any other information you choose to provide.',
        'We automatically collect certain information about your device when you use our services, including IP address, browser type, operating system, referring URLs, and information about your usage of our services.'
      ]
    },
    {
      title: '2. How We Use Your Information',
      content: [
        'We use the information we collect to provide, maintain, and improve our services, including to process transactions, send you technical notices and support messages, respond to your comments and questions, and provide customer service.',
        'We may use your information to communicate with you about products, services, offers, promotions, and events, and provide news and information we think will interest you (you can opt-out of these communications at any time).',
        'We use the information to monitor and analyze trends, usage, and activities in connection with our services, detect, investigate and prevent fraudulent transactions and other illegal activities, and protect the rights and property of Time2Find and others.'
      ]
    },
    {
      title: '3. Information Sharing and Disclosure',
      content: [
        'We do not share your personal information with third parties except as described in this Privacy Policy. We may share information with vendors, consultants, and other service providers who need access to such information to carry out work on our behalf.',
        'We may disclose information if we believe disclosure is in accordance with, or required by, any applicable law or legal process, including lawful requests by public authorities to meet national security or law enforcement requirements.',
        'We may share information in connection with, or during negotiations of, any merger, sale of company assets, financing, or acquisition of all or a portion of our business by another company.'
      ]
    },
    {
      title: '4. Data Security',
      content: [
        'We take reasonable measures to help protect information about you from loss, theft, misuse, unauthorized access, disclosure, alteration, and destruction. However, no internet or electronic storage system is 100% secure, so we cannot guarantee absolute security.',
        'We implement industry-standard security measures including encryption, firewalls, and secure socket layer technology. All data transmissions are encrypted using SSL/TLS protocols.'
      ]
    },
    {
      title: '5. Data Retention',
      content: [
        'We retain personal information for as long as necessary to fulfill the purposes outlined in this Privacy Policy, unless a longer retention period is required or permitted by law.',
        'We will delete or anonymize your personal information when it is no longer needed for the purposes for which we collected it, or when you request deletion, subject to any legal obligations to retain certain information.'
      ]
    },
    {
      title: '6. Your Rights and Choices',
      content: [
        'You have the right to access, update, or delete your personal information at any time. You can do this by logging into your account or contacting us directly.',
        'You can opt-out of receiving promotional communications from us by following the unsubscribe instructions in those communications or by contacting us.',
        'Depending on your location, you may have additional rights under applicable data protection laws, including the right to data portability, the right to restrict processing, and the right to object to processing.'
      ]
    },
    {
      title: '7. International Data Transfers',
      content: [
        'Time2Find is based in the United States, and we process and store information in the U.S. and other countries. If you are located outside the U.S., please be aware that information we collect will be transferred to and processed in the U.S.',
        'We implement appropriate safeguards for international data transfers in accordance with applicable laws, including standard contractual clauses and other approved mechanisms.'
      ]
    },
    {
      title: '8. Children\'s Privacy',
      content: [
        'Our services are not intended for children under the age of 13, and we do not knowingly collect personal information from children under 13. If we become aware that we have collected personal information from a child under 13, we will take steps to delete such information.',
        'If you are a parent or guardian and believe we have collected information about your child, please contact us immediately.'
      ]
    },
    {
      title: '9. Changes to This Privacy Policy',
      content: [
        'We may update this Privacy Policy from time to time. If we make material changes, we will notify you by email (sent to the email address specified in your account) or by means of a notice on our website prior to the change becoming effective.',
        'We encourage you to review this Privacy Policy periodically to stay informed about our information practices and the choices available to you.'
      ]
    },
    {
      title: '10. Contact Us',
      content: [
        'If you have any questions about this Privacy Policy or our privacy practices, please contact us at:',
        'Email: privacy@time2find.com',
        'Phone: +1 (555) 123-4567',
        'Address: Time2Find Global Headquarters, New York, NY'
      ]
    }
  ];

  return (
    <div className="policy-page">
      {/* Hero Banner */}
      <section className="policy-hero-banner">
        <div className="policy-hero-content">
          <div className="policy-badge">
            <i className="fas fa-shield-alt"></i>
            <span>Legal</span>
          </div>
          <h1 className="policy-hero-heading">Privacy Policy</h1>
          <p className="policy-hero-subtext">
            Your privacy is important to us. This policy explains how we collect, use, and protect your personal information.
          </p>
          <div className="policy-meta">
            <span className="policy-meta-item">
              <i className="fas fa-calendar-alt"></i>
              Last Updated: January 11, 2026
            </span>
            <span className="policy-meta-item">
              <i className="fas fa-clock"></i>
              10 min read
            </span>
          </div>
        </div>
      </section>

      <div className="policy-container">
        {/* Introduction */}
        <div className="policy-intro">
          <p>
            At Time2Find, we are committed to protecting your privacy and ensuring the security of your personal information. 
            This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our services, 
            visit our website, or interact with us.
          </p>
          <p>
            By using our services, you agree to the collection and use of information in accordance with this policy. 
            If you do not agree with our policies and practices, please do not use our services.
          </p>
        </div>

        {/* Sections */}
        <div className="policy-sections">
          {sections.map((section, index) => (
            <div key={index} className="policy-section">
              <h2 className="policy-section-title">{section.title}</h2>
              <div className="policy-section-content">
                {section.content.map((paragraph, pIndex) => (
                  <p key={pIndex}>{paragraph}</p>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Summary Box */}
        <div className="policy-summary-box">
          <div className="policy-summary-icon">
            <i className="fas fa-info-circle"></i>
          </div>
          <div className="policy-summary-content">
            <h3 className="policy-summary-title">Summary</h3>
            <p className="policy-summary-text">
              We collect and use your information to provide and improve our services. We do not sell your personal data. 
              You have control over your information and can request access, updates, or deletion at any time. 
              For questions, contact us at privacy@time2find.com.
            </p>
          </div>
        </div>

        {/* Contact CTA */}
        <div className="policy-cta">
          <h3 className="policy-cta-title">Have Questions About Your Privacy?</h3>
          <p className="policy-cta-text">
            Our team is here to help answer any questions you may have about our privacy practices.
          </p>
          <a href="/contact" className="policy-cta-button">
            <i className="fas fa-envelope"></i>
            Contact Privacy Team
          </a>
        </div>
      </div>
    </div>
  );
}

export default PrivacyPolicy;
